import { PlusIcon } from "@heroicons/react/24/outline";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FreeMode, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";

import { useChatActions } from "../../../contexts/chat-actions-context";
import { useUserType } from "../../../hooks/useUserType";
import { useClientTranslations } from "../../../lib/i18n/client";
import { buildAskEvePrefill } from "../../../lib/journey/askEvePrefill";
import type { Journey, JourneyStep, JourneyTask } from "../../../types/journey";
import styles from "./CurrentTasks.module.css";
import { TaskCard } from "./TaskCard";
import { TaskTabsToggle, type TaskTabValue } from "./TaskTabsToggle";
import { TaskViewToggle, type TaskViewValue } from "./TaskViewToggle";

export interface CurrentTasksProps {
	tasks: JourneyTask[];
	journey?: Journey;
	step?: JourneyStep | null;
	/** Opens the (parent-owned) create-task modal when the "+" button is pressed. */
	onAddClick?: () => void;
	onAskEveForTask?: (task: JourneyTask) => void;
	onTaskOpen?: (task: JourneyTask) => void;
	title?: string;
	showAddButton?: boolean;
	onFilterChange?: (filter: TaskTabValue) => void;
	onAskEveAnalytics?: (task: JourneyTask) => void;
	/** Optional — when provided, a "Mark as complete" overflow menu item appears on ongoing task cards. */
	onMarkComplete?: (task: JourneyTask) => void;
	/** Optional — when provided, a "Mark as incomplete" overflow menu item appears on completed task cards. */
	onMarkIncomplete?: (task: JourneyTask) => void;
	/** Optional — when provided, custom (user-created) task cards show an "Edit" menu item. */
	onEditTask?: (task: JourneyTask) => void;
	/** Optional — when provided, custom (user-created) task cards show a "Delete" menu item. */
	onDeleteTask?: (task: JourneyTask) => void;
}

const UNGROUPED_KEY = "__ungrouped__";

interface SubStepGroup {
	key: string;
	title: string;
	visibleTasks: JourneyTask[];
	completedCount: number;
	totalCount: number;
}

function buildGroups(
	allTasks: JourneyTask[],
	filteredTasks: JourneyTask[],
): SubStepGroup[] {
	const order: string[] = [];
	const map = new Map<
		string,
		{
			title: string;
			visibleTasks: JourneyTask[];
			completedCount: number;
			totalCount: number;
		}
	>();
	for (const task of allTasks) {
		const key = task.subStepId ?? UNGROUPED_KEY;
		let entry = map.get(key);
		if (!entry) {
			order.push(key);
			entry = {
				title: task.subStepTitle ?? "",
				visibleTasks: [],
				completedCount: 0,
				totalCount: 0,
			};
			map.set(key, entry);
		}
		entry.totalCount += 1;
		if (task.status === "completed") entry.completedCount += 1;
	}
	for (const task of filteredTasks) {
		const key = task.subStepId ?? UNGROUPED_KEY;
		map.get(key)?.visibleTasks.push(task);
	}
	return order
		.map((key) => {
			const entry = map.get(key);
			if (!entry) return null;
			return {
				key,
				title: entry.title,
				visibleTasks: entry.visibleTasks,
				completedCount: entry.completedCount,
				totalCount: entry.totalCount,
			};
		})
		.filter((g): g is SubStepGroup => g !== null);
}

function GroupProgressCircle({
	pct,
	groupKey,
}: {
	pct: number;
	groupKey: string;
}) {
	const SIZE = 44;
	const CENTER = SIZE / 2;
	const STROKE = 5;
	const STROKE_R = (SIZE - STROKE) / 2;
	const C = 2 * Math.PI * STROKE_R;
	const offset = C - (pct / 100) * C;
	const gradId = `group-pc-track-${groupKey}`;
	return (
		<svg
			width={SIZE}
			height={SIZE}
			viewBox={`0 0 ${SIZE} ${SIZE}`}
			role="img"
			aria-label={`${pct}% complete`}
		>
			<defs>
				<linearGradient id={gradId} x1="50%" y1="0%" x2="50%" y2="100%">
					<stop offset="0%" stopColor="#d6d8e8" />
					<stop offset="50%" stopColor="#eff0fb" />
					<stop offset="100%" stopColor="#eff0fb" />
				</linearGradient>
			</defs>
			<circle
				cx={CENTER}
				cy={CENTER}
				r={STROKE_R}
				fill="none"
				stroke={`url(#${gradId})`}
				strokeWidth={STROKE}
			/>
			<circle
				cx={CENTER}
				cy={CENTER}
				r={STROKE_R}
				fill="none"
				stroke="#5e60bf"
				strokeWidth={STROKE}
				strokeLinecap="round"
				strokeDasharray={C}
				strokeDashoffset={offset}
				transform={`rotate(-90 ${CENTER} ${CENTER})`}
			/>
			<text
				x={CENTER}
				y={CENTER + 3}
				textAnchor="middle"
				fontFamily="Urbanist, sans-serif"
				fontSize="10"
				fontWeight="500"
				fill="#000000"
			>
				{pct}%
			</text>
		</svg>
	);
}

export function CurrentTasks({
	tasks,
	journey,
	step,
	onAddClick,
	onAskEveForTask,
	onTaskOpen,
	title,
	showAddButton = true,
	onFilterChange,
	onAskEveAnalytics,
	onMarkComplete,
	onMarkIncomplete,
	onEditTask,
	onDeleteTask,
}: CurrentTasksProps) {
	const { t, direction } = useClientTranslations("journey");
	const { t: chatT } = useClientTranslations("chat");
	const navigate = useNavigate();
	const { setPrefillQuestion } = useChatActions();
	const { isD2C } = useUserType();

	const [filter, setFilter] = useState<TaskTabValue>("ongoing");
	const [view, setView] = useState<TaskViewValue>("grid");
	const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);

	const filteredTasks = useMemo(
		() => tasks.filter((task) => task.status === filter),
		[tasks, filter],
	);

	const groups = useMemo(
		// Hide groups whose visibleTasks (after filter) are empty so we don't
		// render dangling headers under Completed when a step has only ongoing
		// tasks (or vice versa).
		() =>
			buildGroups(tasks, filteredTasks).filter(
				(g) => g.visibleTasks.length > 0,
			),
		[tasks, filteredTasks],
	);

	const headingText = title ?? t("currentTasks.title");

	const handleAskEve = (task: JourneyTask) => {
		if (onAskEveAnalytics) onAskEveAnalytics(task);
		if (onAskEveForTask) {
			onAskEveForTask(task);
			return;
		}
		const prefill =
			journey != null
				? buildAskEvePrefill({ task, journey, step: step ?? null, t: chatT })
				: task.title;
		setPrefillQuestion(prefill);
		navigate("/chat");
	};

	const handleChevron = (task: JourneyTask) => {
		if (onTaskOpen) onTaskOpen(task);
	};

	const askEveLabel = t("actions.askEve");
	const markCompleteLabel = t("task.actions.markComplete");
	const markIncompleteLabel = t("task.actions.markIncomplete");
	const editLabel = t("task.actions.edit");
	const deleteLabel = t("task.actions.delete");
	const completedLabel = t("task.completed");
	const completedOnTimeLabel = t("task.completedOnTime");

	const renderGroupTasks = (groupTasks: JourneyTask[]) => (
		<div className={styles.swiperWrapper}>
			<Swiper
				modules={[Pagination, FreeMode]}
				freeMode
				pagination={{ clickable: true }}
				slidesPerView="auto"
				spaceBetween={11.993}
				dir={direction}
				threshold={5}
				touchStartPreventDefault={false}
				touchAngle={45}
				className={styles.swiper}
			>
				{groupTasks.map((task) => (
					<SwiperSlide
						key={task.id}
						className={`${view === "grid" ? styles.slideGrid : styles.slide}${isD2C ? ` ${styles.slideCompact}` : ""}`}
					>
						<TaskCard
							task={task}
							view={view}
							onAskEve={handleAskEve}
							onChevronClick={handleChevron}
							askEveLabel={askEveLabel}
							progressLabel={(count, total, unit) =>
								t("currentTasks.taskProgress", { count, total, unit })
							}
							onMarkComplete={
								onMarkComplete ? () => onMarkComplete(task) : undefined
							}
							onMarkIncomplete={
								onMarkIncomplete ? () => onMarkIncomplete(task) : undefined
							}
							onEdit={
								task.isCustom && onEditTask ? () => onEditTask(task) : undefined
							}
							onDelete={
								task.isCustom && onDeleteTask
									? () => onDeleteTask(task)
									: undefined
							}
							editLabel={editLabel}
							deleteLabel={deleteLabel}
							moreOptionsLabel={t("task.actions.moreOptions", {
								title: task.title,
							})}
							markCompleteLabel={markCompleteLabel}
							markIncompleteLabel={markIncompleteLabel}
							completedLabel={completedLabel}
							completedOnTimeLabel={completedOnTimeLabel}
							hideProgress={isD2C}
							compact={isD2C}
						/>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	);

	return (
		<section
			className={styles.section}
			data-step-content="tasks"
			aria-labelledby="current-tasks-heading"
		>
			<div className={styles.toolbarRow}>
				<TaskTabsToggle
					value={filter}
					onChange={(next) => {
						setFilter(next);
						if (onFilterChange) onFilterChange(next);
					}}
					ongoingLabel={t("currentTasks.tabs.ongoing")}
					completedLabel={t("currentTasks.tabs.completed")}
				/>
				<TaskViewToggle
					value={view}
					onChange={setView}
					listLabel={t("currentTasks.viewToggle.list")}
					gridLabel={t("currentTasks.viewToggle.grid")}
				/>
			</div>

			<div className={styles.container}>
				<div className={styles.header}>
					<h2 id="current-tasks-heading" className={styles.heading}>
						{headingText}
					</h2>
					{showAddButton ? (
						<button
							type="button"
							className={styles.addButton}
							aria-label={t("currentTasks.addButton")}
							onClick={() => onAddClick?.()}
						>
							<PlusIcon
								className={styles.addIcon}
								width={20}
								height={20}
								aria-hidden="true"
							/>
						</button>
					) : null}
				</div>

				{filteredTasks.length === 0 && groups.length === 0 ? (
					<p className={styles.empty}>{t(`currentTasks.empty.${filter}`)}</p>
				) : (
					<div className={styles.groups}>
						{groups.map((group, idx) => {
							const pct =
								group.totalCount > 0
									? Math.round((group.completedCount / group.totalCount) * 100)
									: 0;
							const expanded =
								expandedGroupKey === null
									? idx === 0
									: expandedGroupKey === group.key;
							return (
								<section
									key={group.key}
									className={styles.group}
									data-expanded={expanded}
									aria-label={
										group.title
											? t("currentTasks.subStepHeadingAria", {
													title: group.title,
												})
											: undefined
									}
								>
									{group.title ? (
										<button
											type="button"
											className={styles.groupHeader}
											aria-expanded={expanded}
											onClick={() =>
												setExpandedGroupKey(expanded ? "" : group.key)
											}
										>
											{!isD2C && (
												<GroupProgressCircle pct={pct} groupKey={group.key} />
											)}
											<h3 className={styles.groupHeading}>{group.title}</h3>
											<span
												className={styles.groupChevron}
												aria-hidden="true"
												data-expanded={expanded}
											>
												<ChevronDown size={20} strokeWidth={1.5} />
											</span>
										</button>
									) : null}
									{expanded && group.visibleTasks.length > 0
										? renderGroupTasks(group.visibleTasks)
										: null}
								</section>
							);
						})}
					</div>
				)}
			</div>
		</section>
	);
}
